import Image from 'next/image'
import Link from 'next/link'
import { useEffect } from 'react'
import { SITE_URL } from 'src/config/config'
import { IContentItem } from 'src/graphql/generated/hooks'
import { isContentItem, isContentListItemFragment } from 'src/utils/ContentDataFragment'
import { getContentDefaultImageUrl } from 'src/utils/getContentDefaultImageUrl'

import { HtmlContentViewer } from '../global/HtmlContentViewer/HtmlContentViewer'
import { InsightCardTags } from '../Insights/InsightCardTags'

export interface ArticleCardProps {
  item: IContentItem
  cardId: string
  key?: number
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
  subjectMatterPopup?: number
  setHideShowTooltip?: (hide: boolean) => void
}

export const ArticleMobileCard: React.FC<ArticleCardProps> = ({
  item,
  cardId,
  openTooltipCardId,
  setOpenTooltipCardId,
  subjectMatterPopup,
  setHideShowTooltip,
}) => {
  const defaultImageUrl = getContentDefaultImageUrl(item)
  //const logo = useDefaultLogo(defaultImageUrl)
  const contentType = item.contentType?.toLowerCase()
  const logo = `${SITE_URL}/${contentType}_logo.svg`
  const snippet = (isContentListItemFragment(item) ? item?.snippet : isContentItem(item) ? item.snippet : '') || ''

  const contentTags = [item?.subjectMatters, item?.themes, item?.vendorTopics]
    .filter((str) => str && str.trim() !== '')
    .join(', ')

  const makeLinks = (linkTo: string, subjectMatterArray: any, subjectMatterIDs: any) => {
    let goto = 'subject_matter_'
    if (!subjectMatterArray || subjectMatterArray === '' || subjectMatterArray === undefined) {
      return []
    }
    if (linkTo === 'subject_matter') {
      goto = 'know-how/?subject=subject_matter_'
    } else if (linkTo === 'theme') {
      goto = 'legaltech-resources-for-lawyers/?subject=theme_'
    } else if (linkTo === 'vendor_topic') {
      goto = 'resources-for-solution-providers/?vendorTopic='
    }

    return subjectMatterArray?.split(',').map((name: string, index: number) => ({
      name: name.trim(),
      id: subjectMatterIDs?.[index],
      link: `${goto}${subjectMatterIDs?.[index]}`,
    }))
  }

  const subjectMatterLiks = makeLinks('subject_matter', item?.subjectMatters, item?.subjectMatterIds)
  const themesLiks = makeLinks('theme', item?.themes, item?.themeIds)
  const vendorTopicsLiks = makeLinks('vendor_topic', item?.vendorTopics, item?.vendorTopicIds)

  const contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)

  useEffect(() => {
    setOpenTooltipCardId(null)
  }, [subjectMatterPopup])

  return (
    <div className="carousel-card w-[342px] h-[504px] gap-[56px] bg-white rounded-lg pl-[24px] pt-[24px] pr-[24px] pb-[24px]">
      <div className=" w-[294px]">
        <div className="avtar-div  rounded-lg mb-[12px] relative">
          <Link href={`/contents/${item?.slug}`} passHref>
            <Image
              className="rounded-lg object-fill bg-clouds carousel-images w-[294px] h-[172px]"
              src={defaultImageUrl || logo || ''}
              alt="Article"
              width={294}
              height={172}
            />
          </Link>
        </div>

        <div className="flex flex-col justify-center w-full">
          <div className=" self-start h-[90px]">
            <Link href={`/contents/${item?.slug}`} passHref>
              <h2 className="card-heading font-secondary text-[30px] font-bold text-left text-[#011D58] line-clamp-2">
                {item?.title?.trim()}
              </h2>
            </Link>
          </div>

          <div className="flex flex-wrap gap-2 mb-[9px] h-[22px]">
            {contentTags && (
              <InsightCardTags
                key={`${item?.slug}-${item?.id}-carousel-card`}
                cardId={cardId}
                tagItems={contentTags}
                contentTagsLink={contentTagsLink}
                openTooltipCardId={openTooltipCardId}
                setOpenTooltipCardId={setOpenTooltipCardId}
                containerSize={288}
                subjectMatterPopup={subjectMatterPopup}
                setHideShowTooltip={setHideShowTooltip}
                isMobile={true}
              />
            )}
          </div>

          <Link href={`/contents/${item?.slug}`} passHref>
            <HtmlContentViewer
              className="self-end h-[119px] font-primary text-[16px] text-[#333333] font-normal   tracking-0 line-clamp-5  html-show w-full"
              html={snippet}
            />
          </Link>
        </div>
      </div>
    </div>
  )
}
